package co.maytes.standupbot.dashboard.features.rundetail

import co.maytes.standupbot.dashboard.features.standupruns.RunStatusRules
import co.maytes.standupbot.dashboard.features.standupruns.StandupRunsStore
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.shared.dto.standuprun.DiscardDraftResponseDto
import co.maytes.standupbot.shared.dto.standuprun.PostDraftNowResponseDto
import co.maytes.standupbot.shared.dto.standuprun.RegenerateStandupRunRequestDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import co.maytes.standupbot.shared.dto.standuprun.TriggerStandupRunRequestDto
import co.maytes.standupbot.shared.dto.standuprun.TriggerStandupRunResponseDto
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import io.ktor.client.request.header
import kotlinx.coroutines.CoroutineScope
import kotlin.random.Random

class RunDetailContainer(
    scope: CoroutineScope,
    private val api: ApiClient,
    private val runsStore: StandupRunsStore,
) : Container<RunDetailContract.State, RunDetailContract.Intent, RunDetailContract.Action> {

    override val store = scope.store<
            RunDetailContract.State,
            RunDetailContract.Intent,
            RunDetailContract.Action,
            >(initialState = RunDetailContract.State()) {
        configure(name = "RunDetail")
        logging()
        reduce { intent ->
            when (intent) {
                is RunDetailContract.Intent.Load -> {
                    updateState {
                        copy(
                            workspaceId = intent.workspaceId,
                            runId = intent.runId,
                            status = if (detail != null && detail.id == intent.runId)
                                RunDetailContract.Status.Ready
                            else
                                RunDetailContract.Status.Loading,
                        )
                    }
                    load()
                }
                RunDetailContract.Intent.Retry -> {
                    updateState { copy(status = RunDetailContract.Status.Loading) }
                    load()
                }
                RunDetailContract.Intent.Back ->
                    action(RunDetailContract.Action.Navigate("/app/runs"))
                RunDetailContract.Intent.BackToList ->
                    action(RunDetailContract.Action.Navigate("/app/standups"))
                RunDetailContract.Intent.Rerun -> rerun()
                is RunDetailContract.Intent.Regenerate -> regenerate(intent.model)
                RunDetailContract.Intent.DismissRegenerateError ->
                    updateState { copy(regenerateError = null) }
                RunDetailContract.Intent.PostDraftNow -> postDraftNow()
                RunDetailContract.Intent.DiscardDraft -> discardDraft()
            }
        }
    }

    private suspend fun StoreScope<RunDetailContract.State, RunDetailContract.Action>.load() {
        val (ws, runId) = currentState.run { workspaceId to runId }
        if (ws.isBlank() || runId.isBlank()) return

        val cached = runsStore.cachedDetail(runId)
        if (cached != null) {
            updateState { copy(detail = cached, status = RunDetailContract.Status.Ready) }
            return
        }
        when (val res = runsStore.detail(ws, runId)) {
            is Result.Ok ->
                updateState {
                    copy(detail = res.value, status = RunDetailContract.Status.Ready)
                }
            is Result.Err ->
                updateState { copy(status = RunDetailContract.Status.Error(res.error)) }
        }
    }

    private suspend fun StoreScope<RunDetailContract.State, RunDetailContract.Action>.rerun() {
        val state = currentState
        val standupId = state.detail?.standupId ?: return
        if (state.rerunInFlight) return

        updateState { copy(rerunInFlight = true) }
        val key = "rerun-${Random.nextLong().toString(HEX_RADIX)}"
        val res = api.post<TriggerStandupRunRequestDto, TriggerStandupRunResponseDto>(
            path = "/api/v1/workspaces/${state.workspaceId}/standups/$standupId/trigger",
            body = TriggerStandupRunRequestDto(),
        ) { header("Idempotency-Key", key) }
        updateState { copy(rerunInFlight = false) }
        when (res) {
            is Result.Ok -> {
                action(RunDetailContract.Action.Toast("Re-run queued"))
                action(RunDetailContract.Action.Navigate("/app/runs/${res.value.standupId}"))
            }
            is Result.Err ->
                action(RunDetailContract.Action.Toast("Re-run failed: ${res.error.displayMessage}", error = true))
        }
    }

    private suspend fun StoreScope<RunDetailContract.State, RunDetailContract.Action>.regenerate(model: String) {
        val state = currentState
        if (state.workspaceId.isBlank() || state.runId.isBlank() || state.regenerateInFlight) return

        updateState { copy(regenerateInFlight = true, regenerateError = null) }
        val res = api.post<RegenerateStandupRunRequestDto, StandupRunDetailDto>(
            path = "/api/v1/workspaces/${state.workspaceId}/standups/${state.runId}/regenerate",
            body = RegenerateStandupRunRequestDto(model = model),
        )
        updateState { copy(regenerateInFlight = false) }
        when (res) {
            is Result.Ok -> {
                updateState { copy(regenerateError = null) }
                runsStore.invalidateDetail(state.runId)
                when (val reload = runsStore.detail(state.workspaceId, state.runId)) {
                    is Result.Ok -> updateState { copy(detail = reload.value) }
                    is Result.Err -> Unit
                }
                action(RunDetailContract.Action.Toast("Regenerated with $model"))
            }
            is Result.Err -> {
                updateState { copy(regenerateError = res.error.displayMessage) }
                action(RunDetailContract.Action.Toast("Regenerate failed: ${res.error.displayMessage}", error = true))
            }
        }
    }

    private suspend fun StoreScope<RunDetailContract.State, RunDetailContract.Action>.postDraftNow() {
        val state = currentState
        if (state.workspaceId.isBlank() || state.runId.isBlank() || state.draftActionInFlight) return

        updateState { copy(draftActionInFlight = true) }
        val res = api.post<PostDraftNowResponseDto>("/api/v1/workspaces/${state.workspaceId}/runs/${state.runId}/post-now")
        updateState { copy(draftActionInFlight = false) }
        when (res) {
            is Result.Ok -> {
                reloadDetail(state.workspaceId, state.runId)
                action(RunDetailContract.Action.Toast(RunStatusRules.postNowMessage(res.value.posted, res.value.status)))
            }
            is Result.Err ->
                action(RunDetailContract.Action.Toast("Post failed: ${res.error.displayMessage}", error = true))
        }
    }

    private suspend fun StoreScope<RunDetailContract.State, RunDetailContract.Action>.discardDraft() {
        val state = currentState
        if (state.workspaceId.isBlank() || state.runId.isBlank() || state.draftActionInFlight) return

        updateState { copy(draftActionInFlight = true) }
        val res = api.post<DiscardDraftResponseDto>("/api/v1/workspaces/${state.workspaceId}/runs/${state.runId}/discard")
        updateState { copy(draftActionInFlight = false) }
        when (res) {
            is Result.Ok -> {
                reloadDetail(state.workspaceId, state.runId)
                action(RunDetailContract.Action.Toast("Draft discarded. Nothing will be posted for this day"))
            }
            is Result.Err ->
                action(RunDetailContract.Action.Toast("Discard failed: ${res.error.displayMessage}", error = true))
        }
    }

    private suspend fun StoreScope<RunDetailContract.State, RunDetailContract.Action>.reloadDetail(
        workspaceId: String,
        runId: String,
    ) {
        runsStore.invalidateDetail(runId)
        when (val reload = runsStore.detail(workspaceId, runId)) {
            is Result.Ok -> updateState { copy(detail = reload.value) }
            is Result.Err -> Unit
        }
    }

    private companion object {
        const val HEX_RADIX = 16
    }
}
